import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { ASSETS, PRACTITIONER } from "@/data/content";
import { useSite } from "@/context/SiteContext";
import Snippet from "@/components/Snippet";

const LINKS = [
  { to: "/", label: "Home", k: "nav.home" },
  { to: "/about-qhht", label: "About QHHT", k: "nav.about_qhht" },
  { to: "/about-isabelle", label: "About Isabelle", k: "nav.about_isabelle" },
  { to: "/what-to-expect", label: "What to Expect", k: "nav.what_to_expect" },
  { to: "/volunteer", label: "Free Sessions", k: "nav.volunteer" },
  { to: "/faq", label: "FAQ", k: "nav.faq" },
  { to: "/testimonials", label: "Testimonials", k: "nav.testimonials" },
  { to: "/contact", label: "Contact", k: "nav.contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { img, settings } = useSite();
  if (pathname.startsWith("/admin")) return null;

  const logo = img("logo", ASSETS.logo);
  const business = settings.business_name || PRACTITIONER.business;

  return (
    <header className="sticky top-0 z-50 bg-brand-bg/85 backdrop-blur-xl border-b border-brand-line/60">
      <nav className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img src={logo} alt={business} className="h-11 w-11 rounded-full object-cover" />
          <span className="inline-flex flex-col leading-tight">
            <span className="font-heading text-xl text-brand-lavender-dark font-semibold tracking-tight">
              {business}
            </span>
            <span
              className="font-body text-[11px] uppercase tracking-[0.22em] text-brand-slate"
              style={{ textAlignLast: "justify" }}
            >
              <Snippet k="nav.tagline" fallback="QHHT · SOUTH DEVON" />
            </span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `font-body text-sm tracking-wide transition-colors ${
                  isActive ? "text-brand-forest" : "text-brand-slate hover:text-brand-forest"
                }`
              }
            >
              <Snippet k={l.k} fallback={l.label} />
            </NavLink>
          ))}
          <Link
            to="/book"
            data-testid="nav-book-btn"
            className="inline-flex items-center bg-brand-forest-fresh text-white rounded-full px-6 py-2.5 font-body text-sm tracking-wide hover:bg-brand-forest transition-colors"
          >
            <Snippet k="nav.cta" fallback="Book a Session" />
          </Link>
        </div>

        <button
          className="lg:hidden text-brand-ink"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden border-t border-brand-line/60 bg-brand-bg px-6 py-5 space-y-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block py-2.5 font-body text-base ${isActive ? "text-brand-forest" : "text-brand-slate"}`
              }
            >
              <Snippet k={l.k} fallback={l.label} />
            </NavLink>
          ))}
          <Link
            to="/book"
            onClick={() => setOpen(false)}
            className="mt-3 inline-flex items-center bg-brand-forest-fresh text-white rounded-full px-6 py-3 font-body text-sm"
          >
            <Snippet k="nav.cta" fallback="Book a Session" />
          </Link>
        </div>
      )}
    </header>
  );
}
